import {
  ComponentType,
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash';
import { Box, Flex } from '@chakra-ui/react';
import { useInView } from 'react-intersection-observer';
import { GroupedVirtuoso, VirtuosoHandle } from 'react-virtuoso';
import { GroupsInfo, MessageActions, MessageType } from 'types/chat.types';
import { LoadingMoreIndicator } from 'app/components/Chat/LoadingMoreIndicator';
import { useAppDispatch } from 'app/store';
import { updateLastSeenMessageInChannelAction } from 'app/store/Chat/slice';
import { updateLastSeenMessageEffect } from 'app/store/Chat/effects';
import { ContextMenu, useContextMenu } from 'app/components/ChakraTableList';
import { DateSeparator } from '../DateSeparator';
import { scrollToIndex } from '../utils';
import {
  countPrependedMessages,
  mapDateGroupsInfo,
  processMessages,
} from './utils';
import { START_INDEX } from './constants';
import { MessageRenderer } from './MessageRenderer';

interface MessagesListProps {
  messages: MessageType[];
  hasMore?: boolean;
  hasMoreNewer: boolean;
  loadingMore: boolean;
  loadingMoreNewer: boolean;
  messageActions: MessageActions;
  emptyStateComponent: ComponentType;
  virtuosoRef: RefObject<VirtuosoHandle>;
  lastReadTimestamp?: number;
  userGuid?: string;
  loadMore?: () => Promise<void>;
  loadMoreNewer?: () => Promise<void>;
}

export const MessagesList: FC<MessagesListProps> = ({
  messages = [],
  emptyStateComponent,
  hasMore,
  hasMoreNewer,
  loadMore,
  loadingMore,
  loadMoreNewer,
  userGuid = '',
  messageActions,
  virtuosoRef,
  lastReadTimestamp,
}) => {
  const initialTopMostItemIndex = useMemo(
    () => (messages.length > 0 ? messages.length - 1 : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const firstMessageGuid = useMemo(
    () => (messages.length !== 0 ? messages[0].guid : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const processedMessages = useMemo(
    () =>
      processMessages({
        lastReadTimestamp,
        messages,
        userGuid,
      }),
    [lastReadTimestamp, messages, userGuid]
  );

  const atTheBottom = useRef<boolean>(false);
  const [numOfItemsPrepended, setNumOfItemsPrepended] = useState(0);
  const [firstItemIndex, setFirstItemIndex] = useState(START_INDEX);
  const [groupsInfo, setGroupsInfo] = useState<GroupsInfo>(() =>
    mapDateGroupsInfo(processedMessages)
  );

  const dispatch = useAppDispatch();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const contextMenuProps = useContextMenu();

  const debounced = debounce((inView: boolean) => {
    clearTimeout(timeoutRef.current);
    if (inView) {
      dispatch(updateLastSeenMessageEffect());
      timeoutRef.current = setTimeout(() => {
        dispatch(updateLastSeenMessageInChannelAction());
      }, 3000);
    }
  }, 700);

  const { ref } = useInView({
    initialInView: false,
    threshold: 1,
    delay: 300,
    onChange(inView) {
      debounced(inView);
    },
  });

  const startReached = () => {
    if (hasMore && loadMore && !loadingMore) {
      loadMore();
    }
  };

  const endReached = () => {
    if (hasMoreNewer && loadMoreNewer) {
      loadMoreNewer();
    }
  };

  const messageRenderer = useCallback(
    (messageList: MessageType[], virtuosoIndex: number) => (
      <MessageRenderer
        ref={ref}
        userGuid={userGuid}
        virtuosoIndex={virtuosoIndex}
        numOfItemsPrepended={numOfItemsPrepended}
        messageList={messageList}
        contextMenuProps={contextMenuProps}
        messageActions={messageActions}
      />
    ),
    [contextMenuProps, messageActions, numOfItemsPrepended, ref, userGuid]
  );

  useEffect(() => {
    const prependedCount = countPrependedMessages(
      processedMessages,
      firstMessageGuid
    );

    setNumOfItemsPrepended(prependedCount);
    setFirstItemIndex(START_INDEX - prependedCount);
    setGroupsInfo(mapDateGroupsInfo(processedMessages));

    if (atTheBottom.current) {
      scrollToIndex(virtuosoRef, Number.MAX_SAFE_INTEGER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedMessages, atTheBottom, virtuosoRef]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  useEffect(
    () => () => {
      dispatch(updateLastSeenMessageInChannelAction());
    },
    [dispatch]
  );

  return (
    <Flex direction="column" height="full">
      <ContextMenu contextMenuProps={contextMenuProps} />
      <GroupedVirtuoso
        style={{ height: '100%', overflowX: 'hidden' }}
        ref={virtuosoRef}
        groupCounts={groupsInfo.counts}
        groupContent={(index) =>
          index === 0 ? (
            <Box h="1px" />
          ) : (
            <DateSeparator
              text={groupsInfo.titles[index]}
              py={2}
              scrollToGroup={() =>
                scrollToIndex(virtuosoRef, groupsInfo.firstItemsIndexes[index])
              }
            />
          )
        }
        firstItemIndex={firstItemIndex}
        initialTopMostItemIndex={initialTopMostItemIndex}
        components={{
          EmptyPlaceholder: emptyStateComponent,
          Header: loadingMore ? LoadingMoreIndicator : undefined,
        }}
        startReached={startReached}
        endReached={endReached}
        itemContent={(i) => messageRenderer(processedMessages, i)}
        atBottomStateChange={(bottom) => {
          atTheBottom.current = bottom;
        }}
        alignToBottom
        followOutput
        itemSize={(el: HTMLElement) => {
          if (el.getAttribute('data-item-group-index') === '0') {
            return 1;
          }
          return Math.round(el.getBoundingClientRect().height);
        }}
      />
    </Flex>
  );
};
